import React, { useState, useMemo } from 'react';
import { Stethoscope, Plus, Search, Filter, Eye, Edit, Trash2, Calendar, Clock, User, Pill } from 'lucide-react';
import { useConsultations } from '../../hooks/useApi';
import useConsultationsCrud from '../../hooks/useConsultationsCrud';
import { useNotification } from '../../hooks/useNotification';
import { useAuth } from '../../contexts/AuthContext';
import ConsultationAddModal from './ConsultationAddModal';
import ConsultationEditModal from './ConsultationEditModal';
import ConsultationDetailsModal from './ConsultationDetailsModal';
import ConsultationDeleteModal from './ConsultationDeleteModal';
import PrescriptionsModal from './PrescriptionsModal';
import Notification from '../common/Notification';

const ConsultationsList = () => {
  const { user } = useAuth();
  const role = (user?.role || '').toLowerCase();
  const { data: consultations = [], loading, error, refetch } = useConsultations();
  const { addConsultation, updateConsultation, deleteConsultation, isLoading } = useConsultationsCrud();
  const { notification, showNotification, hideNotification } = useNotification();

  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPrescriptionsModal, setShowPrescriptionsModal] = useState(false);

  // Extraire les valeurs uniques pour les filtres
  const baseConsultations = useMemo(() => {
    if (role === 'doctor' && user?.id) {
      return consultations.filter(c => c?.doctor?.user?.id === user.id);
    }
    return consultations;
  }, [consultations, role, user?.id]);

  const statuses = [...new Set(baseConsultations.map(c => c.status))];
  const priorities = [...new Set(baseConsultations.map(c => c.priority))];

  const filteredConsultations = baseConsultations.filter(consultation => {
    const matchesSearch = 
      consultation.patient?.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      consultation.doctor?.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      consultation.chiefComplaint?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !filterStatus || consultation.status === filterStatus;
    const matchesPriority = !filterPriority || consultation.priority === filterPriority;
    return matchesSearch && matchesStatus && matchesPriority;
  });

  const handleAddConsultation = async (formData) => {
    try {
      await addConsultation(formData);
      refetch();
      setShowCreateModal(false);
      showNotification('Consultation ajoutée avec succès', 'success');
    } catch (error) {
      console.error('Erreur lors de l\'ajout:', error);
      showNotification('Erreur lors de l\'ajout de la consultation', 'error');
    }
  };

  const handleEditConsultation = async (consultationId, formData) => {
    try {
      await updateConsultation(consultationId, formData);
      refetch();
      setShowEditModal(false);
      setSelectedConsultation(null);
      showNotification('Consultation modifiée avec succès', 'success');
    } catch (error) {
      console.error('Erreur lors de la modification:', error);
      showNotification('Erreur lors de la modification de la consultation', 'error');
    }
  };

  const handleDeleteConsultation = async (consultationId) => {
    try {
      await deleteConsultation(consultationId);
      refetch();
      setShowDeleteModal(false);
      setSelectedConsultation(null);
      showNotification('Consultation supprimée avec succès', 'success');
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      showNotification('Erreur lors de la suppression de la consultation', 'error');
    }
  };

  const openDetailsModal = (consultation) => {
    setSelectedConsultation(consultation);
    setShowDetailsModal(true);
  };

  const openEditModal = (consultation) => {
    setSelectedConsultation(consultation);
    setShowEditModal(true);
  };

  const openDeleteModal = (consultation) => {
    setSelectedConsultation(consultation);
    setShowDeleteModal(true);
  };

  const openPrescriptionsModal = (consultation) => {
    setSelectedConsultation(consultation);
    setShowPrescriptionsModal(true);
  };

  const closeModals = () => {
    setShowDetailsModal(false);
    setShowEditModal(false);
    setShowCreateModal(false);
    setShowDeleteModal(false);
    setShowPrescriptionsModal(false);
    setSelectedConsultation(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Terminée':
        return 'bg-green-100 text-green-800';
      case 'En cours':
        return 'bg-blue-100 text-blue-800';
      case 'En attente':
        return 'bg-yellow-100 text-yellow-800';
      case 'Annulée':
        return 'bg-red-100 text-red-800';
      case 'Reportée':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Urgente':
        return 'bg-red-100 text-red-800';
      case 'Élevée':
        return 'bg-orange-100 text-orange-800';
      case 'Normale':
        return 'bg-blue-100 text-blue-800';
      case 'Faible':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Gestion des Consultations</h1>
              <p className="text-gray-600 mt-2">Gérez les consultations médicales</p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2"
            >
              <Plus className="h-5 w-5" />
              <span>Nouvelle Consultation</span>
            </button>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Chargement des consultations...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-8">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Erreur de chargement</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        {!loading && !error && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="relative md:col-span-2">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Rechercher une consultation..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Tous les statuts</option>
                  {statuses.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
              <div className="relative">
                <select
                  value={filterPriority}
                  onChange={(e) => setFilterPriority(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Toutes les priorités</option>
                  {priorities.map(priority => (
                    <option key={priority} value={priority}>{priority}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mt-4 text-sm text-gray-500">
              {filteredConsultations.length} consultation(s) trouvée(s)
            </div>
          </div>
        )}

        {/* Consultations List */}
        {!loading && !error && (
          <div className="space-y-4">
            {filteredConsultations.map((consultation) => (
              <div
                key={consultation.id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="p-2 bg-cyan-100 rounded-lg">
                        <Stethoscope className="h-5 w-5 text-cyan-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {consultation.patient?.user?.name || 'Patient inconnu'}
                        </h3>
                        <p className="text-sm text-gray-500">
                          Dr. {consultation.doctor?.user?.name || 'Médecin inconnu'} - {consultation.hospital?.name || 'Non spécifié'}
                        </p>
                      </div>
                    </div>
                    
                    {consultation.chiefComplaint && (
                      <p className="text-sm text-gray-600 mb-3">
                        <strong>Motif :</strong> {consultation.chiefComplaint}
                      </p>
                    )}
                    
                    {consultation.diagnosis && (
                      <p className="text-sm text-gray-600 mb-3">
                        <strong>Diagnostic :</strong> {consultation.diagnosis}
                      </p>
                    )}

                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        <span>{new Date(consultation.consultationDate).toLocaleDateString('fr-FR')}</span>
                      </div>
                      <div className="flex items-center">
                        <Clock className="h-4 w-4 mr-1" />
                        <span>{new Date(consultation.consultationDate).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col items-end space-y-2 ml-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(consultation.status)}`}>
                      {consultation.status}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(consultation.priority)}`}>
                      {consultation.priority}
                    </span>
                    <div className="flex space-x-2 mt-2">
                      <button
                        onClick={() => openDetailsModal(consultation)}
                        className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                        title="Voir les détails"
                      >
                        <Eye className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => openPrescriptionsModal(consultation)}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        title="Prescriptions"
                      >
                        <Pill className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => openEditModal(consultation)}
                        className="p-2 text-secondary-600 hover:bg-secondary-50 rounded-lg transition-colors"
                        title="Modifier"
                      >
                        <Edit className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => openDeleteModal(consultation)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && !error && filteredConsultations.length === 0 && (
          <div className="text-center py-12">
            <Stethoscope className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune consultation trouvée</h3>
            <p className="text-gray-500">Essayez de modifier vos critères de recherche</p>
          </div>
        )}
      </div>

      {/* Modals */}
      <ConsultationAddModal
        isOpen={showCreateModal}
        onClose={closeModals}
        onSave={handleAddConsultation}
        loading={isLoading}
      />

      <ConsultationEditModal
        consultation={selectedConsultation}
        isOpen={showEditModal}
        onClose={closeModals}
        onSave={handleEditConsultation}
        loading={isLoading}
      />

      <ConsultationDetailsModal
        consultation={selectedConsultation}
        isOpen={showDetailsModal}
        onClose={closeModals}
      />

      <ConsultationDeleteModal
        consultation={selectedConsultation}
        isOpen={showDeleteModal}
        onClose={closeModals}
        onConfirm={handleDeleteConsultation}
        loading={isLoading}
      />

      <PrescriptionsModal
        consultation={selectedConsultation}
        isOpen={showPrescriptionsModal}
        onClose={closeModals}
      />

      {/* Notification */}
      <Notification
        isVisible={notification.isVisible}
        onClose={hideNotification}
        type={notification.type}
        title={notification.title}
        message={notification.message}
      />
    </div>
  );
};

export default ConsultationsList;